import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Wrench,
  Cable,
  Bot,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  TrendingUp,
} from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import { SOLUTIONS_COPY } from "@/data/solutionsCopy";

const SITE = "https://oceandata.ca";
const FRAME_ICONS = { "frame-fix": Wrench, "frame-connect": Cable, "frame-automate": Bot };

function ServiceBlock({ service, index, stickLabels }) {
  const { setOpen } = useSimplify();
  const [openHood, setOpenHood] = useState(false);
  const flip = index % 2 === 1;

  return (
    <Reveal>
      <article
        data-testid={service.id}
        className="grid grid-cols-12 gap-8 lg:gap-12 py-14 lg:py-20 border-t border-slate-200 items-start"
      >
        <div className={`col-span-12 lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
            {service.frame} · {service.name}
          </p>
          <h3 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl mb-5">
            {service.headline}
          </h3>
          <p className="text-base text-slate-600 leading-relaxed max-w-xl mb-7">
            {service.outcome}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid={`${service.id}-cta`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold px-7 py-3.5 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              {service.cta}
              <ArrowRight size={16} />
            </button>
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-brand-teal">
              <TrendingUp size={14} />
              {service.chip}
            </span>
          </div>
        </div>

        <div className={`col-span-12 lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <button
              data-testid={`${service.id}-hood-toggle`}
              onClick={() => setOpenHood((v) => !v)}
              aria-expanded={openHood}
              className="w-full flex items-center justify-between px-6 sm:px-8 py-5 text-left hover:bg-brand-tealsoft/50 transition-colors duration-300"
            >
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-brand-navy font-semibold">
                {service.hoodTitle}
              </span>
              <motion.span animate={{ rotate: openHood ? 180 : 0 }} transition={{ duration: 0.3 }}>
                <ChevronDown size={17} className="text-brand-teal" />
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {openHood && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-6 sm:px-8 pb-7">
                    <p
                      data-testid={`${service.id}-hood-content`}
                      className="text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-5"
                    >
                      {service.hood}
                    </p>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[service.stick.copy, service.stick.visual, service.stick.film, service.stick.why].map((value, i) => (
                        <div key={i} className="rounded-xl bg-brand-tealsoft/60 border border-brand-tealborder/60 p-4">
                          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-1.5">
                            {stickLabels[i]}
                          </p>
                          <p className="text-xs text-slate-600 leading-relaxed">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function SolutionsPage({ lang = "en" }) {
  const { setOpen } = useSimplify();
  const t = SOLUTIONS_COPY[lang] || SOLUTIONS_COPY.en;

  useEffect(() => {
    document.title = t.docTitle;
    document.documentElement.lang = lang;
    const links = [];
    Object.entries(t.hreflangs).forEach(([hreflang, path]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = `${SITE}${path}`;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    });
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, [t, lang]);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t.schemaName,
    inLanguage: t.schemaLang,
    itemListElement: t.services.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Service",
        name: s.name,
        serviceType: s.frame,
        description: `${s.headline} ${s.outcome}`,
        provider: { "@type": "Organization", name: "Ocean Data", url: SITE },
        areaServed: "Canada",
      },
    })),
  };

  return (
    <main className="pt-16 sm:pt-20" lang={lang}>
      <section className="py-20 lg:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            {t.eyebrow}
          </p>
          <h1
            data-testid="solutions-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="solutions-headline-line-1">{t.h1a}</MaskedLine>
            <MaskedLine delay={0.3} testId="solutions-headline-line-2">
              <span className="text-brand-teal">{t.h1b}</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.heroSub}
          </motion.p>
        </div>
      </section>

      <section className="pb-8 lg:pb-12 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
          {t.frames.map((f, i) => {
            const Icon = FRAME_ICONS[f.id];
            return (
              <Reveal key={f.id} delay={i * 0.08}>
                <div
                  data-testid={f.id}
                  className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="w-11 h-11 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-6">
                    <Icon size={19} strokeWidth={2} />
                  </span>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-navy font-bold mb-2">
                    {f.name}
                  </p>
                  <p className="font-display font-medium text-lg text-brand-navy leading-snug mb-3">
                    {f.line}
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.detail}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="pb-20 lg:pb-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          {t.services.map((s, i) => (
            <ServiceBlock key={s.id} service={s} index={i} stickLabels={t.stickLabels} />
          ))}
        </div>
      </section>

      <section className="bg-brand-navy text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-12 gap-8 items-center">
          <Reveal className="col-span-12 lg:col-span-7">
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-2xl sm:text-3xl lg:text-4xl mb-4">
              {t.closeTitle}
            </h2>
            <p className="text-base text-slate-300 leading-relaxed max-w-xl">
              {t.closeBody}
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
              {t.links.map((l) => (
                <Link key={l.testid} to={l.to} data-testid={l.testid} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold hover:text-white transition-colors">
                  {l.label} <ArrowUpRight size={15} />
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 flex lg:justify-end">
            <button
              data-testid="solutions-bottom-cta"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              {t.closeCta}
              <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </main>
  );
}
